import PropTypes from 'prop-types';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown, Input, Radio, Space } from 'antd';
import { AutoSizer, List } from 'react-virtualized';
import { useTranslation } from 'react-i18next';
import { get, mapValues, omit } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import { CUSTOM_I18N_KEY_SEPARATOR, extractI18nKeys, extractI18nKeysFromProject } from '@icp/i18n';
import Icon from '../Icon';
import Resizable from '../Resizable';
import SearchBox from '../SearchBox';
import { generateTokenByOriginalText, generateTranslation } from './util';

const I18nKeySelect = forwardRef(function I18nKeySelect(
  {
    defaultValue,
    fallbackText,
    i18nResources,
    projectId,
    allowedLanguages,
    pbcToken,
    formEntityToken,
    excludeKeysIn,
  },
  ref,
) {
  const { t, i18n } = useTranslation(['icp-form-designer']);
  const navigate = useNavigate();

  const [openPicker, setOpenPicker] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [i18nValues, setI18nValues] = useState(defaultValue);
  const [i18nPreviewLng, setI18nPreviewLng] = useState(() =>
    i18nResources?.[i18n.language] ? i18n.language : Object.keys(i18nResources || {})[0],
  );

  const [actionBtnLoading, setActionBtnLoading] = useState(false);
  const [actionBtnLoading2, setActionBtnLoading2] = useState(false);
  const [loadingTranslation, setLoadingTranslation] = useState({}); // { [language]: Boolean }

  useImperativeHandle(ref, () => ({
    getValue: () => i18nValues,
  }));

  const [collapseNsMap, setCollapseNsMap] = useState({});

  const listItems = useMemo(() => {
    const searchTextLowerCased = searchText?.toLowerCase();
    return Object.entries(i18nResources?.[i18nPreviewLng] || {}).flatMap(([ns, resource]) => {
      const collapsed = collapseNsMap[ns];
      const nsItem = {
        type: 'namespace',
        data: { ns, collapsed: !!collapsed },
      };

      if (collapsed) {
        return [nsItem];
      }

      function flat(translationObj, prefix = '') {
        return Object.entries(translationObj).flatMap(([translationKey, translation]) => {
          if (translation == null) return [];
          if (typeof translation === 'object') {
            return flat(translation, `${prefix}${translationKey}.`);
          }
          if (typeof translation === 'string') {
            const joinedKey = prefix + translationKey;
            if (
              searchTextLowerCased &&
              !joinedKey.toLowerCase().includes(searchTextLowerCased) &&
              !translation.toLowerCase().includes(searchTextLowerCased)
            ) {
              return [];
            }
            return [
              {
                type: 'translation-key',
                data: { ns, translationKey: joinedKey, translation },
              },
            ];
          }
          // unknown type
          return [];
        });
      }

      return [nsItem, ...flat(resource)];
    });
  }, [i18nResources, i18nPreviewLng, collapseNsMap, searchText]);

  const handleGenerateKey = () => {
    setActionBtnLoading(true);

    generateTokenByOriginalText(fallbackText)
      .then((token) =>
        [pbcToken, formEntityToken, token].filter(Boolean).join(CUSTOM_I18N_KEY_SEPARATOR),
      )
      .then((newI18nKey) => setI18nValues((prev) => ({ ...prev, key: newI18nKey })))
      .finally(() => setActionBtnLoading(false));
  };

  const handleGenerateUniqueKey = () => {
    setActionBtnLoading(true);
    generateTokenByOriginalText(fallbackText)
      .then((token) =>
        [pbcToken, formEntityToken, token].filter(Boolean).join(CUSTOM_I18N_KEY_SEPARATOR),
      )
      .then(async (prefix) => {
        const allI18nEntries = await extractI18nKeysFromProject(projectId);

        // 加上当前FormEntity/Page草稿中的
        const allI18nEntriesInDraft = extractI18nKeys(excludeKeysIn || {});
        allI18nEntries.push(...allI18nEntriesInDraft);

        const allI18nKeys = new Set(
          allI18nEntries
            .filter((x) => x.namespace === i18nValues?.ns || 'default')
            .map((x) => x.key),
        );

        let newI18nKey = [prefix, uuidv4().split('-')[0]].join(CUSTOM_I18N_KEY_SEPARATOR);
        while (allI18nKeys.has(newI18nKey)) {
          newI18nKey = [prefix, uuidv4().split('-')[0]].join(CUSTOM_I18N_KEY_SEPARATOR);
        }

        return newI18nKey;
      })
      .then((newI18nKey) => setI18nValues((prev) => ({ ...prev, key: newI18nKey })))
      .finally(() => setActionBtnLoading(false));
  };

  const handleGenerateTranslation = async (locale) => {
    if (!fallbackText) return;
    setLoadingTranslation((prev) => ({ ...prev, [locale]: true }));
    await generateTranslation({
      text: fallbackText,
      targetLanguage: locale,
      callback: (translation) => {
        setI18nValues((prev) => ({ ...prev, [locale]: translation }));
      },
    }).finally(() => {
      setLoadingTranslation((prev) => ({ ...prev, [locale]: false }));
    });
  };

  const actionMenuItems =
    i18nValues?.ns !== 'standard' && window.__bot_conf__?.enableAI
      ? [
          {
            key: 'generate',
            label: t('generate', { ns: 'icp-common' }),
            onClick: handleGenerateKey,
          },
          {
            key: 'generate-unique',
            label: t('aigc.generate-unique', { ns: 'icp-form-designer' }),
            onClick: handleGenerateUniqueKey,
          },
        ]
      : [];

  const actionMenuProps = {
    onClick: (e) => actionMenuItems.find((x) => x.key === e.key)?.onClick?.(),
    items: actionMenuItems.map((x) => omit(x, 'onClick')),
  };

  const actionMenuItems2 = [
    window.__bot_conf__?.enableAI && {
      key: 'generate',
      label: t('generate', { ns: 'icp-common' }),
      disabled: !fallbackText,
      onClick: () => {
        setActionBtnLoading2(true);
        Promise.allSettled(
          allowedLanguages.map((locale) => handleGenerateTranslation(locale)),
          //
        ).finally(() => {
          setActionBtnLoading2(false);
        });
      },
    },
    {
      key: 'clear',
      label: t('clear', { ns: 'icp-common' }),
      onClick: () => {
        setI18nValues((prev) => ({
          ...prev,
          ...Object.fromEntries(allowedLanguages.map((locale) => [locale, undefined])),
        }));
      },
    },
  ].filter(Boolean);

  const actionMenuProps2 = {
    onClick: (e) => actionMenuItems2.find((x) => x.key === e.key)?.onClick?.(),
    items: actionMenuItems2.map((x) => omit(x, 'onClick')),
  };

  return (
    <div className="icp-i18n-picker-popover">
      <div className="icp-i18n-picker-popover-input">
        <fieldset>
          <legend>{t('original-text', { ns: 'icp-components' })}</legend>
          <div className="readonly-text">{fallbackText}</div>
        </fieldset>
        <fieldset>
          <legend>{t('i18n-picker.use-key-and-resources')}</legend>
          <div>
            <div>Key:</div>
            <Input
              value={i18nValues?.key || ''}
              onChange={(e) =>
                setI18nValues((prev) => ({ ...prev, key: e.target.value || undefined }))
              }
              maxLength={255}
              disabled={actionBtnLoading}
            />
          </div>
          <div>
            <div>Namespace:</div>
            <Input
              value={i18nValues?.ns || ''}
              onChange={(e) =>
                setI18nValues((prev) => ({ ...prev, ns: e.target.value || undefined }))
              }
              maxLength={255}
              disabled={actionBtnLoading}
            />
          </div>

          <div className="icp-i18n-picker-popover-btn-bar">
            {actionMenuProps.items.length ? (
              <Dropdown size="small" trigger={['click']} menu={actionMenuProps}>
                <Button size="small" loading={actionBtnLoading}>
                  <Space>
                    {t('action.title', { ns: 'icp-form-designer' })}
                    <Icon name="ant:down-outlined" size={14} />
                  </Space>
                </Button>
              </Dropdown>
            ) : null}
            <div onClick={() => setOpenPicker((prev) => !prev)}>
              <span>{t('i18n-picker.pick-from-resource')}</span>
              {openPicker ? (
                <Icon name="oct:triangle-left" size={16} />
              ) : (
                <Icon name="oct:triangle-right" size={16} />
              )}
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend>{t('i18n-picker.use-direct-translation')}</legend>
          {allowedLanguages.map((lng) => (
            <div key={lng}>
              <div>{lng}:</div>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  value={i18nValues?.[lng] || ''}
                  onChange={(e) =>
                    setI18nValues((prev) => ({ ...prev, [lng]: e.target.value || undefined }))
                  }
                  maxLength={255}
                />
                {window.__bot_conf__?.enableAI ? (
                  <Button
                    disabled={!fallbackText}
                    loading={loadingTranslation[lng]}
                    onClick={() => handleGenerateTranslation(lng)}
                  >
                    {t('generate', { ns: 'icp-common' })}
                  </Button>
                ) : null}
              </Space.Compact>
            </div>
          ))}

          <div className="icp-i18n-picker-popover-btn-bar">
            {actionMenuProps2.items.length ? (
              <Dropdown size="small" trigger={['click']} menu={actionMenuProps2}>
                <Button size="small" loading={actionBtnLoading2}>
                  <Space>
                    {t('action.title', { ns: 'icp-form-designer' })}
                    <Icon name="ant:down-outlined" size={14} />
                  </Space>
                </Button>
              </Dropdown>
            ) : null}
          </div>
        </fieldset>

        <fieldset>
          <legend
            style={{
              color: 'var(--readonly-text-color)',
            }}
          >
            {t('i18n-picker.preview')}
          </legend>
          {allowedLanguages.map((lng) => (
            <div key={lng}>
              <div>{lng}:</div>
              <div className="readonly-text">
                {i18nValues?.[lng] ??
                  get(i18nResources?.[lng]?.[i18nValues?.ns || 'default'], i18nValues?.key) ??
                  fallbackText}
              </div>
            </div>
          ))}
        </fieldset>
      </div>

      {openPicker ? (
        <Resizable placement="left" defaultSize={600}>
          <div className="icp-i18n-picker-popover-picker">
            <div className="icp-h-center" style={{ gap: 4 }}>
              <span style={{ color: '#999' }}>{t('i18n-picker.pick-from-resource')}</span>

              <Radio.Group
                size="small"
                optionType="button"
                options={Object.keys(i18nResources || {})}
                value={i18nPreviewLng}
                onChange={(e) => setI18nPreviewLng(e.target.value)}
              />

              <button
                className="icp-action-button"
                onClick={() => {
                  navigate(`/center/project/${projectId}/i18n`);
                }}
              >
                <Icon name="oct:pencil" size={16} />
              </button>
            </div>

            <div className="icp-i18n-picker-popover-actions">
              <SearchBox
                className="icp-i18n-picker-popover-search"
                open={true}
                suppressSpeech={true}
                onSearch={setSearchText}
              />
              <button
                className="icp-action-button"
                onClick={() =>
                  setCollapseNsMap(mapValues(i18nResources?.[i18nPreviewLng] || {}, () => true))
                }
              >
                <Icon name="oct:fold" size={16} />
              </button>
              <button className="icp-action-button" onClick={() => setCollapseNsMap({})}>
                <Icon name="oct:unfold" size={16} />
              </button>
            </div>

            <div style={{ flex: 1 }}>
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    width={width}
                    height={height}
                    rowCount={listItems.length}
                    rowHeight={24}
                    rowRenderer={(params) => {
                      const { index, key, style } = params;
                      const item = listItems[index];

                      return (
                        <div key={key} style={style}>
                          {item.type === 'namespace' ? (
                            <div
                              className="icp-i18n-picker-popover-namespace"
                              onClick={() =>
                                setCollapseNsMap((prev) => ({
                                  ...prev,
                                  [item.data.ns]: !item.data.collapsed,
                                }))
                              }
                            >
                              {item.data.collapsed ? (
                                <Icon name="oct:triangle-right" size={16} />
                              ) : (
                                <Icon name="oct:triangle-down" size={16} />
                              )}
                              <span>{item.data.ns}</span>
                            </div>
                          ) : null}
                          {item.type !== 'namespace' ? (
                            <div className="icp-i18n-picker-popover-item">
                              <span
                                className={clsx(
                                  'icp-i18n-picker-popover-translation-key',
                                  i18nValues?.key === item.data.translationKey &&
                                    (i18nValues?.ns || 'default') === item.data.ns &&
                                    'selected',
                                )}
                                onClick={() =>
                                  setI18nValues((prev) => ({
                                    ...prev,
                                    ns: item.data.ns === 'default' ? undefined : item.data.ns,
                                    key: item.data.translationKey,
                                  }))
                                }
                              >
                                {item.data.translationKey}
                              </span>
                              :&nbsp;
                              <span>{item.data.translation}</span>
                            </div>
                          ) : null}
                        </div>
                      );
                    }}
                  />
                )}
              </AutoSizer>
            </div>
          </div>
        </Resizable>
      ) : null}
    </div>
  );
});

I18nKeySelect.propTypes = {
  defaultValue: PropTypes.shape({
    key: PropTypes.string,
    ns: PropTypes.string,
    /* other i18n locale translation e.g.
      zh-CN: PropTypes.string,
      en-US: PropTypes.string,
      ja-JP: PropTypes.string,
    */
  }),
  fallbackText: PropTypes.string,
  i18nResources: PropTypes.shape({}),
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  allowedLanguages: PropTypes.arrayOf(PropTypes.string),
  pbcToken: PropTypes.string,
  formEntityToken: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  excludeKeysIn: PropTypes.any,
  formEntity: PropTypes.shape({
    token: PropTypes.string,
  }),
};

export default I18nKeySelect;
